import React, { useState, useEffect, useCallback } from 'react';
import {
  PiggyBank, Plus, AlertTriangle, AlertCircle, CheckCircle2,
  Calendar, Trash2, TrendingUp, TrendingDown, DollarSign
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export default function Budgets() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [monthYear, setMonthYear] = useState(() => new Date().toISOString().substring(0, 7));
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Aux data for new budget
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category_id: '',
    cost_center_id: '',
    budgeted_amount: '',
    alert_threshold_percent: 80,
    month_year: new Date().toISOString().substring(0, 7),
    owner_company_id: '',
  });

  // Load companies & categories
  useEffect(() => {
    (async () => {
      try {
        const [oc, cats, ccs] = await Promise.all([
          api.get('/owner-companies').catch(() => ({ data: { data: [] } })),
          api.get('/categories').catch(() => ({ data: { data: [] } })),
          api.get('/cost-centers').catch(() => ({ data: { data: [] } })),
        ]);
        setOwnerCompanies(oc.data?.data || oc.data || []);
        setCategories(cats.data?.data || cats.data || []);
        setCostCenters(ccs.data?.data || ccs.data || []);
      } catch (err) {
        console.error('Erro ao buscar dados auxiliares', err);
      }
    })();
  }, []);

  const loadBudgets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month_year: monthYear };
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;

      const res = await api.get('/financial/budgets', { params });
      const raw = res.data;
      const list = raw?.data || raw?.items || (Array.isArray(raw) ? raw : []);
      setBudgets(list);
    } catch (err) {
      console.error('Erro ao carregar orçamentos', err);
      toast.error('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId, monthYear]);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.budgeted_amount);
    if (!amt || amt <= 0) {
      toast.error('Informe um valor orçado válido');
      return;
    }
    if (!form.category_id && !form.cost_center_id) {
      toast.error('Selecione uma categoria ou centro de custo');
      return;
    }

    setSaving(true);
    try {
      await api.post('/financial/budgets', {
        ...form,
        budgeted_amount: amt,
        alert_threshold_percent: parseInt(form.alert_threshold_percent) || 80,
      });
      toast.success('Meta orçamentária cadastrada com sucesso');
      setModalOpen(false);
      setForm({
        category_id: '',
        cost_center_id: '',
        budgeted_amount: '',
        alert_threshold_percent: 80,
        month_year: monthYear,
        owner_company_id: '',
      });
      loadBudgets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar orçamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm('Deseja excluir esta meta orçamentária?')) return;
    try {
      await api.delete(`/financial/budgets/${b.id}`);
      toast.success('Orçamento excluído');
      loadBudgets();
    } catch {
      toast.error('Erro ao excluir orçamento');
    }
  };

  // Totals
  const totalBudgeted = budgets.reduce((acc, b) => acc + (b.budgeted_amount || 0), 0);
  const totalRealized = budgets.reduce((acc, b) => acc + (b.realized_amount || 0), 0);
  const totalRemaining = totalBudgeted - totalRealized;
  const warningCount = budgets.filter(b => b.is_warning || b.is_exceeded).length;

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Orçamentos (Budgets)"
        subtitle="Definição de metas orçamentárias mensais por categoria e centro de custo"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onRefresh={loadBudgets}
      />

      {/* Toolbar & Selector de Mês */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-neutral-400" />
          <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">Competência:</span>
          <Input
            type="month"
            value={monthYear}
            onChange={(e) => setMonthYear(e.target.value)}
            className="h-8 w-40 text-xs"
          />
        </div>

        <Button
          size="sm"
          onClick={() => {
            setForm(prev => ({ ...prev, month_year: monthYear }));
            setModalOpen(true);
          }}
          className="h-9 bg-brand hover:bg-[#A81E23] text-white text-xs gap-1.5"
        >
          <Plus className="h-4 w-4" />
          + Definir Novo Orçamento
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Total Orçado no Mês</p>
          <p className="text-2xl font-bold mt-1 text-neutral-900 dark:text-neutral-100">
            {formatCurrency(totalBudgeted)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{budgets.length} metas definidas</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Total Realizado (Gasto)</p>
          <p className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
            {formatCurrency(totalRealized)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{totalBudgeted > 0 ? `${((totalRealized / totalBudgeted) * 100).toFixed(1)}% do orçamento` : '-'}</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Saldo Restante Disponível</p>
          <p className={`text-2xl font-bold mt-1 ${totalRemaining >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600'}`}>
            {formatCurrency(totalRemaining)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{totalRemaining >= 0 ? 'Dentro do planejado' : 'Orçamento estourado'}</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Metas em Alerta / Estouradas</p>
          <p className={`text-2xl font-bold mt-1 ${warningCount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {warningCount}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{warningCount > 0 ? 'Exigem atenção imediata' : 'Tudo sob controle'}</p>
        </div>
      </div>

      {/* Grid de Orçamentos com Barra de Progresso */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-44 rounded-xl shimmer" />
          ))
        ) : budgets.length === 0 ? (
          <div className="col-span-full py-12 text-center text-sm text-neutral-400 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800">
            <PiggyBank className="h-10 w-10 text-neutral-300 mx-auto mb-2" />
            Nenhuma meta orçamentária cadastrada para {monthYear}. Clique em "+ Definir Novo Orçamento".
          </div>
        ) : (
          budgets.map((b) => {
            const isExceeded = b.is_exceeded;
            const isWarning = b.is_warning && !isExceeded;

            return (
              <div
                key={b.id}
                className={`rounded-xl bg-white dark:bg-neutral-900 border p-5 shadow-xs flex flex-col justify-between ${
                  isExceeded
                    ? 'border-red-300 dark:border-red-900/60 ring-1 ring-red-300 dark:ring-red-900/40'
                    : isWarning
                    ? 'border-amber-300 dark:border-amber-900/60'
                    : 'border-neutral-200 dark:border-neutral-800'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-sm text-neutral-900 dark:text-neutral-100">
                        {b.category_name || b.cost_center_name || 'Geral'}
                      </h4>
                      <p className="text-xs text-neutral-500">
                        {b.cost_center_name ? `Centro: ${b.cost_center_name}` : 'Categoria'}
                      </p>
                    </div>

                    {isExceeded ? (
                      <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">
                        Estourado
                      </Badge>
                    ) : isWarning ? (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                        Alerta ({b.percent_used}%)
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                        Ok ({b.percent_used}%)
                      </Badge>
                    )}
                  </div>

                  {/* Barra de Progresso */}
                  <div className="mt-4 space-y-1.5">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-neutral-600 dark:text-neutral-400">
                        Gasto: {formatCurrency(b.realized_amount)}
                      </span>
                      <span className="text-neutral-900 dark:text-neutral-100">
                        Meta: {formatCurrency(b.budgeted_amount)}
                      </span>
                    </div>

                    <div className="h-2.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isExceeded
                            ? 'bg-red-600'
                            : isWarning
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(b.percent_used || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-neutral-400">Saldo:</span>
                    <span className={`ml-1 font-bold ${b.remaining_amount >= 0 ? 'text-neutral-700 dark:text-neutral-300' : 'text-red-600'}`}>
                      {formatCurrency(b.remaining_amount)}
                    </span>
                  </div>

                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => handleDelete(b)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Excluir meta"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Novo Orçamento */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Definir Nova Meta Orçamentária</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Estabeleça limites de gastos por categoria ou centro de custo para acompanhamento mensal.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-3.5 text-xs">
            <div>
              <Label className="text-xs">Competência (Mês/Ano) *</Label>
              <Input
                type="month"
                value={form.month_year}
                onChange={e => setForm({ ...form, month_year: e.target.value })}
                required
                className="h-8 text-xs mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Categoria de Despesa</Label>
              <Select
                value={form.category_id}
                onValueChange={v => setForm({ ...form, category_id: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Centro de Custo</Label>
              <Select
                value={form.cost_center_id}
                onValueChange={v => setForm({ ...form, cost_center_id: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Selecione o centro de custo" />
                </SelectTrigger>
                <SelectContent>
                  {costCenters.map(cc => (
                    <SelectItem key={cc.id} value={cc.id}>
                      {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Empresa (Opcional)</Label>
              <Select
                value={form.owner_company_id}
                onValueChange={v => setForm({ ...form, owner_company_id: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Todas as empresas (Consolidado)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas</SelectItem>
                  {ownerCompanies.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.cnpj ? `(${c.cnpj})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Valor Orçado (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  value={form.budgeted_amount}
                  onChange={e => setForm({ ...form, budgeted_amount: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>

              <div>
                <Label className="text-xs">Gatilho de Alerta (%)</Label>
                <Input
                  type="number"
                  min="50"
                  max="100"
                  value={form.alert_threshold_percent}
                  onChange={e => setForm({ ...form, alert_threshold_percent: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">
                {saving ? 'Salvando...' : 'Salvar Orçamento'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
